// SPDX-FileCopyrightText: Copyright 2025 OP Financial Group (https://op.fi). All Rights Reserved.
//
// SPDX-License-Identifier: LicenseRef-OpAllRightsReserved

import React, { useContext } from 'react';
import { Checkbox, CheckboxGroup } from '@op/opds.opux';
import { ContextWrapper, AdminData, AdminAgreementType, Role } from '../../../context/ContextWrapper';
import { roleHeading } from '../../../context/Roles';
import message from '../../../messages';
import { deepCopy } from '../../../util/Util';

const Roles: React.FC<any> = ({ admin, updateAdminRoles, agreementType }) => {
    const { contractData, updateInformation } = useContext(ContextWrapper);

    const rolesData: AdminAgreementType = deepCopy(admin.agreementTypes[agreementType]?.rolesData);
    const adminData: AdminData = { ...admin };
    const allRoles: Role[] = deepCopy(admin.agreementTypes[agreementType]?.rolesData?.roles);

    const handleCheckbox = (row: Role, checked: boolean) => {
        rolesData.roles.forEach((role: Role, index: number) => {
            if (
                role.selectedForAgreementType === row.selectedForAgreementType &&
                role.name === row.name &&
                role.heading === row.heading
            ) {
                rolesData.roles[index].isChecked = checked;
            }
        });

        if (!checked) {
            rolesData.allRolesSelected = false;
        } else if (!rolesData.roles.some((role: Role) => !role.isChecked)) {
            rolesData.allRolesSelected = true;
        }

        return rolesData;
    };

    const renderCheckBox = (allRoles: Role[], heading: string): React.ReactElement => {
        return (
            <CheckboxGroup className="admin-role-checkbox-margin-left">
                {!!allRoles &&
                    allRoles
                        .filter((e) => e.heading === heading)
                        .map((role) => {
                            const disabled = agreementType === '';
                            const checked = agreementType === '' ? false : role.isChecked;
                            const handleChange = (_e: any, checked: boolean) => {
                                handleCheckbox(role, checked);
                                adminData.agreementTypes[agreementType].rolesData = rolesData;

                                updateAdminRoles(adminData);
                                updateInformation((state: any) => {
                                    // TODO: Refactor this code to not nest functions more than 4 levels deep.
                                    const copy = deepCopy(state.companyTypeSelected);
                                    copy.showWarning = false;

                                    return {
                                        ...state,
                                        companyTypeSelected: copy,
                                    };
                                });
                            };

                            return (
                                <Checkbox
                                    key={role.name}
                                    labelProps={{ className: 'check-box-label' }}
                                    disabled={disabled}
                                    onChange={handleChange}
                                    checked={checked}
                                    size="small"
                                >
                                    {message(role.name)}
                                </Checkbox>
                            );
                        })}
            </CheckboxGroup>
        );
    };

    const checkAll = (checked: boolean): void => {
        rolesData.roles = rolesData.roles.map((role: Role) => {
            return { ...role, isChecked: checked };
        });
    };

    return (
        <div>
            <Checkbox
                size="small"
                data-testid="rolesCheckbox"
                disabled={agreementType === ''}
                checked={agreementType === '' && !!rolesData ? false : rolesData?.allRolesSelected}
                labelProps={{ className: 'check-box-label' }}
                onChange={(_e: any, checked: boolean) => {
                    updateInformation((state: any) => {
                        const copy = deepCopy(contractData.companyTypeSelected);
                        copy.showWarning = false;

                        return {
                            ...state,
                            companyTypeSelected: copy,
                        };
                    });

                    checkAll(checked);
                    adminData.agreementTypes[agreementType].rolesData.allRolesSelected = checked;
                    adminData.agreementTypes[agreementType].rolesData.roles = rolesData.roles;
                    updateAdminRoles(adminData);
                }}
            >
                {message('selectAll')}
            </Checkbox>
            {roleHeading.map((heading) => (
                <div key={heading} data-testid="rolesDiv">
                    <h4 className="admin-information-heading" data-testid="rolesH4">
                        {message(heading)}
                    </h4>
                    {renderCheckBox(allRoles, heading)}
                </div>
            ))}
        </div>
    );
};

export default Roles;
